/**
 * Brand-color normalization
 * ─────────────────────────
 * The docx importer bakes the example doc's literal heading colors into the
 * contract. To keep templates on the WORKSPACE brand (white-label consistency),
 * accent-role literal colors are normalized to the `{{protoss:brand.primary_color}}`
 * token, which resolves to the workspace brand color at render. Body/incidental
 * (neutral) colors are left alone.
 *
 * Used at import (default) and as the studio's one-click "Normalize to brand"
 * remediation for templates authored before this existed.
 */
export const BRAND_PRIMARY_TOKEN = '{{protoss:brand.primary_color}}';
/** A near-gray / near-black / near-white color is incidental, not a brand accent. */
function isNeutralHex(hex) {
    const h = hex.replace(/^#/, '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(h))
        return true; // not a plain hex (e.g. already a token)
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const sat = max === 0 ? 0 : (max - min) / max;
    return sat < 0.15 || max < 30 || min > 230;
}
/** A literal hex accent color (not neutral, not already a token). */
function isLiteralAccent(color) {
    if (!color || color.includes('{{'))
        return false;
    return !isNeutralHex(color);
}
function isHeading(child) {
    return child.type === 'paragraph' && !!child.heading;
}
function visitHeadingRuns(contract, visit) {
    for (const section of contract.sections ?? []) {
        for (const child of section.children ?? []) {
            if (!isHeading(child))
                continue;
            for (const run of child.children ?? []) {
                if (run && typeof run === 'object' && !('type' in run))
                    visit(run);
            }
        }
    }
}
/** How many heading runs carry an off-brand literal accent color. */
export function countLiteralAccentColors(contract) {
    let n = 0;
    visitHeadingRuns(contract, (run) => {
        if (isLiteralAccent(run.color))
            n += 1;
    });
    return n;
}
/**
 * The most frequent off-brand literal heading color (uppercase hex, no `#`), or null.
 * The candidate an admin could "promote" to be the workspace brand color.
 */
export function findDominantAccentColor(contract) {
    const counts = new Map();
    visitHeadingRuns(contract, (run) => {
        if (isLiteralAccent(run.color)) {
            const hex = run.color.replace(/^#/, '').toUpperCase();
            counts.set(hex, (counts.get(hex) ?? 0) + 1);
        }
    });
    let best = null;
    let bestCount = 0;
    for (const [hex, count] of counts) {
        if (count > bestCount) {
            best = hex;
            bestCount = count;
        }
    }
    return best;
}
/**
 * Replace off-brand literal heading colors with the brand-color token (immutable).
 * Returns the new contract + how many runs were normalized.
 */
export function normalizeAccentColorsToBrandToken(contract) {
    const next = JSON.parse(JSON.stringify(contract));
    let normalized = 0;
    visitHeadingRuns(next, (run) => {
        if (isLiteralAccent(run.color)) {
            run.color = BRAND_PRIMARY_TOKEN;
            normalized += 1;
        }
    });
    return { contract: next, normalized };
}
//# sourceMappingURL=normalize-brand-color.js.map